import { EPP_DISCOUNT } from './internetPricing';

export const TV_GIFT_OFFER = Object.freeze({
    label: '55" Samsung 4K HDR TV (Gift on purchase)',
    fibreInternetIds: new Set(['internet-1-5g', 'internet-500', 'internet-3g', 'internet-1g']),
    internet500Id: 'internet-500',
    excludedPackageKey: 'core+3theme',
    smallPackageKeys: new Set(['core', 'core+pick5', 'core+1premium']),
    largePackageKeys: new Set([
        'core+2theme+1premium',
        'core+1theme+2premium',
        'core+4theme+1premium',
        'core+3theme+2premium',
        'core+7theme+1premium',
        'core+theme11+2premium'
    ])
});

export const normalizeTvPriceAdjustmentRule = (adjustment = {}) => {
    const amount = Number(adjustment?.amount);
    return {
        enabled: Boolean(adjustment?.enabled),
        amount: Number.isFinite(amount) && amount > 0 ? amount : 0,
        label: String(adjustment?.label || '').trim()
    };
};

export const getInternetTvPriceAdjustment = (internetPlan) => {
    const adjustment = normalizeTvPriceAdjustmentRule(internetPlan?.tvPriceAdjustment);
    if (!adjustment.enabled || adjustment.amount <= 0) return null;

    return {
        ...adjustment,
        label: adjustment.label || `${internetPlan?.name || 'Selected Internet'} TV adjustment`
    };
};

export const getTvGiftOfferEligibility = (internetPlan, tvPlan) => {
    if (!internetPlan) {
        return { eligible: false, amount: 0, reason: 'Select fibre Internet and an eligible TV package.' };
    }
    if (!TV_GIFT_OFFER.fibreInternetIds.has(internetPlan.id)) {
        return { eligible: false, amount: 0, reason: 'Available only with fibre Internet + TV.' };
    }
    if (!tvPlan) {
        return { eligible: false, amount: 0, reason: 'Select an eligible TV package.' };
    }
    if (tvPlan.packageKey === TV_GIFT_OFFER.excludedPackageKey) {
        return { eligible: false, amount: 0, reason: 'Not available with Core + 3 Theme Packs.' };
    }
    const isInternet500 = internetPlan.id === TV_GIFT_OFFER.internet500Id;
    if (TV_GIFT_OFFER.smallPackageKeys.has(tvPlan.packageKey)) {
        const amount = isInternet500 ? 25 : 33;
        return { eligible: true, amount, reason: `Adds $${amount} to the selected TV package.` };
    }
    if (TV_GIFT_OFFER.largePackageKeys.has(tvPlan.packageKey)) {
        const amount = isInternet500 ? 30 : 38;
        return { eligible: true, amount, reason: `Adds $${amount} to the selected TV package.` };
    }
    return { eligible: false, amount: 0, reason: 'This TV package is not eligible for the gift offer.' };
};

export const getTvGiftAdjustment = (internetPlan, tvPlan, giftSelected) => {
    const eligibility = getTvGiftOfferEligibility(internetPlan, tvPlan);
    if (!giftSelected || !eligibility.eligible) return null;
    return { amount: eligibility.amount, label: TV_GIFT_OFFER.label };
};

export const getTvDisplayPrice = (plan, tvPriceAdjustment = null, tvGiftAdjustment = null) => (
    Number(plan?.price || 0)
    + Number(tvPriceAdjustment?.amount || 0)
    + Number(tvGiftAdjustment?.amount || 0)
);

export function getTvPriceBreakdown(selectedTv, eppEnabled, tvPriceAdjustment = null, tvGiftAdjustment = null) {
    if (!selectedTv) return null;

    const basePrice = Number(selectedTv.price || 0);
    const adjustmentAmount = Number(tvPriceAdjustment?.amount || 0);
    const giftAdjustmentAmount = Number(tvGiftAdjustment?.amount || 0);
    const baseTotal = basePrice + adjustmentAmount + giftAdjustmentAmount;
    const eppDiscount = eppEnabled ? Math.min(EPP_DISCOUNT, baseTotal) : 0;

    return {
        basePrice,
        adjustmentAmount,
        adjustmentLabel: tvPriceAdjustment?.label || '',
        giftAdjustmentAmount,
        giftAdjustmentLabel: tvGiftAdjustment?.label || '',
        baseTotal,
        eppDiscount,
        finalTotal: Math.max(baseTotal - eppDiscount, 0)
    };
}
